import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAccounts, fetchRiskSettings } from "../api/api";
import CustomSelect from "@/components/ui/CustomSelect";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import {
    accountIdsEqual,
    findAccountById,
    normalizeAccountList,
    orderAccountsByPreferredDefault,
    resolveInitialAccountId,
} from "../utils/accountSelection";

/* ── tiny platform colour map ── */
const platformColors = {
    MT5:     { bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/30" },
    cTrader: { bg: "bg-purple-500/15", text: "text-purple-400", border: "border-purple-500/30" },
    Rithmic: { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30" },
    Binance: { bg: "bg-yellow-500/15", text: "text-yellow-400", border: "border-yellow-500/30" },
    InteractiveBrokers: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" },
};
const defaultPlatformColor = { bg: "bg-slate-500/15", text: "text-slate-400", border: "border-slate-500/30" };

const AccountSelection = ({ 
    selectedAccount, 
    setSelectedAccount, 
    accountBalance, 
    setAccountBalance, 
    accountEquity, 
    setAccountEquity,
    setRiskPercentFromSettings,
    displayMode = "full", // "full" or "compact"
    className = "",
    selectClassName = "",
    buttonClassName = "",
    dropdownMinWidth,
    dropdownMaxWidth,
    showFullOptionLabels = false,
    preferDefaultOnInitialLoad = false,
}) => {
    const [accounts, setAccounts] = useState([]);
    const { defaultAccountId, loaded: preferencesLoaded = true } = useUserPreferences() || {};
    const hasAppliedInitialSelectionRef = useRef(false);
    const defaultAccountKey = defaultAccountId === null || defaultAccountId === undefined ? '' : String(defaultAccountId);

    useEffect(() => {
        const loadAccounts = async () => {
            try {
                const response = await fetchAccounts();
                setAccounts(normalizeAccountList(response));
            } catch (error) {
                console.error("Error fetching accounts:", error);
                setAccounts([]);
            }
        };
        loadAccounts();
    }, []);

    useEffect(() => {
        hasAppliedInitialSelectionRef.current = false;
    }, [defaultAccountKey]);

    useEffect(() => {
        if (!preferencesLoaded || accounts.length === 0) return;

        const selectedExists = Boolean(findAccountById(accounts, selectedAccount));
        const shouldPreferDefault = preferDefaultOnInitialLoad && !hasAppliedInitialSelectionRef.current;
        const shouldSelect = !selectedAccount || !selectedExists || shouldPreferDefault;
        const nextAccountId = resolveInitialAccountId(accounts, selectedAccount, defaultAccountId, {
            preferDefault: shouldPreferDefault,
        });

        hasAppliedInitialSelectionRef.current = true;

        if (shouldSelect && nextAccountId && !accountIdsEqual(selectedAccount, nextAccountId)) {
            setSelectedAccount(nextAccountId);
        }
    }, [
        accounts,
        defaultAccountId,
        preferencesLoaded,
        preferDefaultOnInitialLoad,
        selectedAccount,
        setSelectedAccount,
    ]);

    useEffect(() => {
        if (selectedAccount && setRiskPercentFromSettings) {
            fetchRiskSettings(selectedAccount)
                .then(settings => {
                    if (settings && typeof settings.risk_percent !== 'undefined') {
                        setRiskPercentFromSettings(settings.risk_percent);
                    }
                })
                .catch(error => console.error("Error fetching risk settings:", error));
        }
    }, [selectedAccount, setRiskPercentFromSettings]);

    /* Build options with metadata for rendering */
    const accountOptions = useMemo(() => orderAccountsByPreferredDefault(accounts, defaultAccountId).map(account => ({
        value: account.id,
        label: account.name,
        platform: account.platform,
        isDefault: Boolean(account.is_default) || accountIdsEqual(account.id, defaultAccountId),
    })), [accounts, defaultAccountId]);

    /* ── Rich dropdown item ── */
    const renderOption = useCallback((option, { selected }) => {
        const colors = platformColors[option.platform] || defaultPlatformColor;
        const labelClassName = showFullOptionLabels ? 'min-w-0 whitespace-nowrap' : 'min-w-0 truncate';
        return (
            <span className="flex items-center gap-2.5 w-full pr-2 py-0.5">
                <span className={`${selected ? 'font-semibold' : 'font-normal'} ${labelClassName}`}>
                    {option.label}
                </span>
                <span className="flex items-center gap-1.5 shrink-0 ml-auto">
                    {option.isDefault && (
                        <span className="text-[10px] font-semibold leading-none px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30">
                            DEFAULT
                        </span>
                    )}
                    <span className={`text-[10px] font-medium leading-none px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                        {option.platform}
                    </span>
                </span>
            </span>
        );
    }, [showFullOptionLabels]);

    /* ── Trigger display (selected value) ── */
    const renderSelected = useCallback((option) => {
        const colors = platformColors[option.platform] || defaultPlatformColor;
        return (
            <span className="flex items-center gap-2 min-w-0 overflow-hidden">
                <span className="truncate min-w-0">{option.label}</span>
                <span className={`shrink-0 text-[10px] font-medium leading-none px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                    {option.platform}
                </span>
            </span>
        );
    }, []);

    const placeholder = displayMode === "compact"
        ? (accounts.length === 0 ? "Loading..." : "Select Account")
        : "Account:";

    return (
        <div className={`text-textPrimary min-w-0 ${displayMode === "compact" ? "w-auto" : ""} ${className}`}>
            <CustomSelect
                options={accountOptions}
                value={selectedAccount || ""}
                onChange={setSelectedAccount}
                placeholder={placeholder}
                className={`${displayMode === "compact" ? "min-w-0 sm:min-w-[200px] w-full sm:w-auto" : "min-w-0 sm:min-w-[240px] w-full sm:w-auto"} ${selectClassName}`}
                buttonClassName={buttonClassName}
                dropdownMinWidth={dropdownMinWidth}
                dropdownMaxWidth={dropdownMaxWidth}
                itemHeight={48}
                maxDropdownHeight={480}
                renderOption={renderOption}
                renderSelected={renderSelected}
            />
        </div>
    );
};

export default AccountSelection;
